/**
 * The theme (System Configuration > Appearance): the template's layout options, applied
 * to the page as the Theme Customizer (right-bar.vue) does -- the layout store, and the
 * data-* attributes on <html> the styles read.
 */

export const DEFAULT_THEME = {
  layout: 'vertical',
  mode: 'light',
  width: 'fluid',
  position: 'fixed',
  topbar: 'light',
  sidebar_size: 'lg',
  sidebar_view: 'default',
  sidebar_color: 'dark',
  sidebar_image: 'none',
  preloader: 'disable',
  // The colors: the template's own, or the AAI logo's red and black (palette-aai.css).
  palette: 'default',
};

// People's own themes, by user: kept through sign-out (see clearStorageKeepingThemes).
const USER_THEMES_KEY = 'userThemes';

/** Applies a theme to the layout store and the page. */
export function applyTheme(theme, store) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  const html = document.documentElement;

  if (store) {
    const layout = store.state.layout || {};
    const set = (action, key, value) => { if (layout[key] !== value) store.dispatch(`layout/${action}`, { [key]: value }); };
    set('changeLayoutType', 'layoutType', t.layout);
    set('changeMode', 'mode', t.mode);
    set('changeLayoutWidth', 'layoutWidth', t.width);
    set('changePosition', 'position', t.position);
    set('changeTopbar', 'topbar', t.topbar);
    set('changeSidebarSize', 'sidebarSize', t.sidebar_size);
    set('changeSidebarView', 'sidebarView', t.sidebar_view);
    set('changeSidebarColor', 'sidebarColor', t.sidebar_color);
    set('changeSidebarImage', 'sidebarImage', t.sidebar_image);
    set('changePreloader', 'preloader', t.preloader);
  }

  html.setAttribute('data-layout', t.layout);
  html.setAttribute('data-layout-mode', t.mode);
  html.setAttribute('data-layout-width', t.width);
  html.setAttribute('data-layout-position', t.position);
  html.setAttribute('data-topbar', t.topbar);
  // The vertical layout sets data-topbar="dark" on <body> when it mounts, which the
  // styles also match: set it there too, or the topbar stays dark.
  document.body.setAttribute('data-topbar', t.topbar);
  html.setAttribute('data-layout-style', t.sidebar_view);
  html.setAttribute('data-sidebar', t.sidebar_color);
  html.setAttribute('data-sidebar-image', t.sidebar_image);
  html.setAttribute('data-preloader', t.preloader);
  html.setAttribute('data-palette', t.palette === 'aai' ? 'aai' : 'default');
  // The horizontal layout has no sidebar size.
  if (t.layout !== 'horizontal') html.setAttribute('data-sidebar-size', t.sidebar_size);
  try { localStorage.setItem('data-preloader', t.preloader); } catch (e) { /* this page only */ }
}

/** The layout store's current settings, as a theme (to keep a person's own theme). */
export function themeFromStore(store) {
  const l = store?.state?.layout || {};
  return {
    layout: l.layoutType, mode: l.mode, width: l.layoutWidth, position: l.position, topbar: l.topbar,
    sidebar_size: l.sidebarSize, sidebar_view: l.sidebarView, sidebar_color: l.sidebarColor,
    sidebar_image: l.sidebarImage, preloader: l.preloader,
    // Not in the layout store (the Theme Customizer doesn't set it): as applied.
    palette: document.documentElement.getAttribute('data-palette') || 'default',
  };
}

function allUserThemes() {
  try {
    return JSON.parse(localStorage.getItem(USER_THEMES_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

/** The signed-in person's own theme (null: none saved). */
export function savedUserTheme() {
  const id = localStorage.getItem('userid');
  return id ? allUserThemes()[id] || null : null;
}

export function saveUserTheme(theme) {
  const id = localStorage.getItem('userid');
  if (!id) return;
  try { localStorage.setItem(USER_THEMES_KEY, JSON.stringify({ ...allUserThemes(), [id]: theme })); } catch (e) { /* this page only */ }
}

/** Clears localStorage (signing out) but keeps people's own themes and the sign-in design. */
export function clearStorageKeepingThemes() {
  // People's own themes, and which sign-in page design to show (System Configuration).
  const keep = [USER_THEMES_KEY, 'loginVersion'];
  const kept = keep.map(k => [k, localStorage.getItem(k)]);
  localStorage.clear();
  kept.forEach(([k, v]) => {
    if (v !== null) {
      try { localStorage.setItem(k, v); } catch (e) { /* lost */ }
    }
  });
}
